import { FieldCollector } from './FieldCollector.js'
import { ComponentArray } from '../Common/ComponentArray.js'
import { FieldGroup } from '../Common/Field.js'
import { isObject, removeElem } from '../SharedUtils.js'

// Components can mark a ComponentArray element as protected from agent
// removal by exposing isEditable, either as a plain property (e.g.
// WallLayer, for its fixed first/last boundary layers) or a method (e.g.
// Schedule, for its built-in default schedules). Absent isEditable, an
// element is removable.
function isElemEditable(elem) {
  if (typeof elem.isEditable === 'function') return elem.isEditable()
  if ('isEditable' in elem) return elem.isEditable
  return true
}

export class ChangeApplier {
  // Recursively apply a plain-object value tree onto a project object.
  // - ComponentArray keys with array values: createNew() for each element, recurse
  // - FieldGroup keys: apply each of its fields by key, same as a Field below
  // - Anything implementing applyAgentValue (any Field/subclass — see
  //   Field.applyAgentValue()/FieldWithVariableUnits — or a non-Field
  //   whole-object component like LocationData): await it with the raw value
  // - Plain object keys: recurse into the child object
  // A key that fails doesn't stop the rest from being applied — errors are
  // collected and thrown together at the end, so the new item is as complete
  // as possible and the agent learns exactly which keys to fix.
  static async apply(obj, values) {
    if (!values || !isObject(values)) return
    const errors = []
    for (const [key, val] of Object.entries(values)) {
      try {
        await ChangeApplier._applyKey(obj, key, val)
      } catch (err) {
        errors.push(`${key}: ${err.message}`)
      }
    }
    if (errors.length) {
      throw new Error(errors.join('; '))
    }
  }

  static async _applyKey(obj, key, val) {
    const target = obj[key]
    if (target instanceof ComponentArray && Array.isArray(val)) {
      for (const itemValues of val) {
        const newItem = target.createNew()
        await ChangeApplier.apply(newItem, itemValues)
      }
    } else if (target instanceof FieldGroup && isObject(val)) {
      for (const [fieldKey, fieldVal] of Object.entries(val)) {
        console.log(`Updating field ${key}/${fieldKey} to`, fieldVal)
        await target.get(fieldKey).applyAgentValue(fieldVal)
      }
    } else if (typeof target?.applyAgentValue === 'function') {
      console.log(`Applying agent value for ${key}:`, val)
      await target.applyAgentValue(val)
    } else if (isObject(target) && isObject(val)) {
      await ChangeApplier.apply(target, val)
    }
  }
}

export class ChangerTool {
  // Apply an array of changes to the project.
  // Each change has a change_type of 'field', 'add', or 'remove'.
  // Returns an array of error objects for any changes that failed.
  static async applyChanges(project, changes) {
    const [, componentsMap] = FieldCollector.collect(project)
    const errors = []

    const parsedRemoves = []

    for (const change of changes) {
      const changeType = change.change_type ?? 'field'
      try {
        if (changeType === 'field') {
          const field = componentsMap.get(change.component_path)
          if (!field) {
            errors.push({ component_path: change.component_path, error: 'Field not found' })
            continue
          }
          if (typeof field.applyAgentValue === 'function') {
            console.log(`Applying agent value at ${change.component_path}:`, change.new_value)
            await field.applyAgentValue(change.new_value)
          } else {
            errors.push({ component_path: change.component_path, error: 'Target does not support field-style updates' })
          }
        } else if (changeType === 'add') {
          const arr = componentsMap.get(change.component_path)
          if (!(arr instanceof ComponentArray)) {
            errors.push({ component_path: change.component_path, error: 'Not a ComponentArray' })
            continue
          }
          console.log(`Adding new item to ${change.component_path}`)
          const newItem = arr.createNew(change.new_item_name)
          if (change.new_value) {
            console.log(`Created newItem. Applying new_value: `, newItem, change.new_value);
            await ChangeApplier.apply(newItem, change.new_value)
          }
        } else if (changeType === 'remove') {
          const lastBracket = change.component_path.lastIndexOf('[')
          if (lastBracket === -1) {
            errors.push({ component_path: change.component_path, error: 'component_path must end with [index]' })
            continue
          }
          const arrayPath = change.component_path.slice(0, lastBracket)
          const index = parseInt(change.component_path.slice(lastBracket + 1, -1))
          const arr = componentsMap.get(arrayPath)
          if (!(arr instanceof ComponentArray)) {
            errors.push({ component_path: change.component_path, error: 'Array not found' })
            continue
          }
          if (index < 0 || index >= arr.length) {
            errors.push({ component_path: change.component_path, error: 'Index out of bounds' })
            continue
          }
          const elemToRemove = arr[index]
          if (!isElemEditable(elemToRemove)) {
            errors.push({ component_path: change.component_path, error: 'This item is fixed and cannot be removed.' })
            continue
          }
          parsedRemoves.push({ arr, elem: elemToRemove, component_path: change.component_path })
        }
      } catch (err) {
        // component_path is included whenever the change carried one (every
        // change type does) — without it, an exception thrown deep inside
        // e.g. applyAgentValue() (as opposed to an explicit errors.push()
        // above) would report only "a field change failed", not which one.
        errors.push({ change_type: changeType, component_path: change.component_path, error: err.message })
      }
    }

    // Apply removals last so that non-remove changes (using indices) are unaffected.
    for (const { arr, elem, component_path } of parsedRemoves) {
      try {
        console.log(`Removing item at ${component_path}`)
        removeElem(arr, elem)
      } catch (err) {
        errors.push({ change_type: 'remove', error: err.message })
      }
    }

    return errors
  }
}
